import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import { dispatch } from 'store'
import { userSelectors } from 'store/features/user/selectors'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { aiAvatarThunks } from 'store/features/aiAvatar/thunks'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { REF_LINKS } from 'consts/refLinks'
import {
  SPEECH_FREE_MAX_LENGTH,
  SPEECH_MAX_LENGTH
} from 'store/features/aiAvatar/consts'
import { getSpeechPreviewText } from './consts'
import { useTrialAudio } from './useTrialAudio'

export const useSpeech = () => {
  const isFreeUser = useSelector(userSelectors.isFreeUser)
  const speakerId = useSelector(aiAvatarSelectors.voiceSpeakerId)
  // Cloned-voice URL to preview with instead of the catalog speaker — only set
  // while the Voice clone sub-tab is active with a clone attached.
  const refAudio = useSelector(aiAvatarSelectors.activeRefAudio)
  const speechText = useSelector(aiAvatarSelectors.speechText)
  const openUpgradePopup = useOpenUpgradePopup()
  const { playState, progress, play, stop } = useTrialAudio()

  // Keep the text in local state for snappy typing and debounce-sync it into
  // redux (`generationParams.speechText`) while the user types.
  const [text, setText] = useState(speechText)

  const debouncedSyncToRedux = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(aiAvatarThunks.setSpeechText(value))
      }, 400),
    []
  )

  const onChangeText = useCallback(
    (value: string) => {
      setText(value)
      debouncedSyncToRedux(value)
    },
    [debouncedSyncToRedux]
  )

  // Sync local state when redux changes from outside (e.g. reset/restore).
  useEffect(() => {
    setText(speechText)
  }, [speechText])

  const maxLength = isFreeUser ? SPEECH_FREE_MAX_LENGTH : SPEECH_MAX_LENGTH
  const showUpgradeLink = isFreeUser && text.length >= maxLength
  const isPlayDisabled = text.trim().length === 0

  const onTogglePlay = useCallback(() => {
    if (isPlayDisabled || playState === 'loading') return
    if (playState === 'idle') {
      play(getSpeechPreviewText(text), speakerId, refAudio)
    } else {
      stop()
    }
  }, [isPlayDisabled, playState, play, stop, text, speakerId, refAudio])

  // Reset the play control if the user clears the text.
  useEffect(() => {
    if (isPlayDisabled && playState !== 'idle') {
      stop()
    }
  }, [isPlayDisabled, playState, stop])

  const onUpgradeClick = useCallback(() => {
    openUpgradePopup({
      ref: REF_LINKS.V_EDITOR_AVATAR_UPGRADE_FOR_LONGER_VIDEOS
    })
  }, [openUpgradePopup])

  return {
    text,
    onChangeText,
    maxLength,
    showUpgradeLink,
    playState,
    progress,
    isPlayDisabled,
    onTogglePlay,
    onUpgradeClick
  }
}
